import { useCallback, useContext, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Label } from '../Label/Label';
import { RadioGroupContext } from './RadioGroup';

const Wrapper = styled.div<{ disabled: boolean }>`
  display: flex;
  gap: 9px;
  align-items: center;
  cursor: pointer;

  &[disabled],
  label[disabled] {
    cursor: not-allowed;
  }
`;

const Radio = styled.input<{ checked: boolean; disabled: boolean }>`
  appearance: none;
  width: 24px;
  height: 24px;
  border-radius: 50px;
  margin: 0;
  padding: 4px !important;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.vars.newColor.darkGS3};

  ${({ disabled, theme }) => {
    if (disabled) {
      return css`
        cursor: not-allowed;
        background-color: ${theme.vars.newColor.lightGS2};
      `;
    }
  }}

  &:hover {
    ${({ checked, disabled, theme }) => {
      if (!checked) {
        return css`
          border: 1px solid ${!disabled ? theme.vars.newColor.blue2 : theme.vars.newColor.darkGS3};
          background-color: ${({ theme }) => theme.vars.newColor.lightGS2};
        `;
      }
    }}
  }

  &:checked {
    background-color: ${({ theme }) => theme.vars.newColor.blue1};
    border: none;
    :after {
      content: '';
      display: block;
      border-radius: 50%;
      width: 100%;
      height: 100%;
      background-color: ${({ theme }) => theme.vars.newColor.white};

      ${({ disabled, theme }) => {
        if (disabled) {
          return css`
            background-color: ${theme.vars.newColor.lightGS2};
            border: 1px solid ${theme.vars.newColor.lightGS1};
          `;
        }
      }}
    }
  }
`;

interface ICommonProps {
  /** Is the radio button checked */
  selected?: boolean;
}

export interface RadioButtonProps extends ICommonProps, IntrinsicProps {
  /** The label name for the radio button */
  label?: string;
  /** ID of of the radio button. */
  id?: string;
  /** Disables the radio button. */
  disabled?: boolean;
  /** Event fired when the radio button is clicked */
  onClick?: () => void;
  /** Value of the radio button. */
  value?: string;
}

export const RadioButton = ({ id, disabled, selected, label, onClick, ...props }: RadioButtonProps) => {
  const context = useContext(RadioGroupContext);
  useEffect(() => {
    if (context && !id) {
      throw new Error('ID is required for RadioGroup');
    }
  }, [context, id]);

  const isSelected = useMemo(() => {
    return selected !== null && selected !== undefined ? selected : context?.selectedId === id;
  }, [context?.selectedId, id, selected]);

  const handleOnClick = useCallback(() => {
    if (disabled) {
      return;
    }

    if (context) {
      context.setSelectedId(id);
    } else {
      onClick();
    }
  }, [context, disabled, id, onClick]);

  return (
    <Wrapper data-testid="RadioButton" disabled={disabled} onClick={handleOnClick}>
      <div style={{ width: 24, height: 24 }}>
        <Radio type="radio" id={id} disabled={disabled} checked={isSelected} onChange={handleOnClick} {...props} />
      </div>
      {label && <Label for={id} name={label} disabled={disabled} width="100%" />}
    </Wrapper>
  );
};
